/**
 * Модель личного пространства My Type2X.
 *
 * Пространство — это записи, привязанные к владельцу, а не папка на сервере
 * (решение AD-005, ТЗ FR-WS-04). Гость владеет пространством через
 * installationId, зарегистрированный пользователь — через userId.
 *
 * Состав пространства задан требованием (ТЗ FR-WS-03) и собран в `Workspace`
 * ниже: baseline, журнал занятий и контрольных тестов, текущие и лучшие
 * показатели, прогресс по единицам, уровни и XP, достижения, серии, место в
 * рейтингах, состояние trial и настройки приватности, языка и клавиатуры.
 */

import type { DayActivity, UnlockedAchievement } from './achievements.js';
import type {
  Baseline,
  ThemePreference,
  EntitlementSnapshot,
  InstallationId,
  KeyboardProfile,
  LanguageCode,
  MasteryStat,
  MasteryUnit,
  UserId,
  XpTransaction,
} from '@type2x/shared-contracts';
import { DEFAULT_THEME } from '@type2x/shared-contracts';

/** Кому принадлежит пространство. */
export type WorkspaceOwner =
  | { readonly kind: 'guest'; readonly installationId: InstallationId }
  | { readonly kind: 'user'; readonly userId: UserId; readonly installationId: InstallationId };

/** Настройки приватности (ТЗ FR-LB-06). */
export interface PrivacySettings {
  /** Участие в таблицах лидеров. По умолчанию выключено. */
  readonly leaderboardOptIn: boolean;
  /** Псевдоним для публичного показа. */
  readonly displayName: string | null;
  /** Согласие на аналитику. По умолчанию не дано. */
  readonly analyticsConsent: boolean;
}

/**
 * Внешний вид интерфейса (ТЗ UX-01, UX-03).
 *
 * Живёт в пространстве, а не в профиле языка: тема и фокус-режим от языка не
 * зависят. И не в `localStorage`: это часть личного пространства, как язык
 * интерфейса, — иначе настройка потерялась бы при переносе прогресса.
 */
export interface Appearance {
  readonly theme: ThemePreference;
  /**
   * Фокус-режим экрана набора: без панелей вокруг текста.
   *
   * Переживает перезагрузку: пользователь включает его не на одно
   * упражнение, а потому что так ему удобнее печатать.
   */
  readonly focusMode: boolean;
  /**
   * Звук на промах во время набора.
   *
   * При слепом наборе глаза заняты текстом, и цвет ошибки человек замечает
   * через несколько символов. Выключатель обязателен: звук, который нельзя
   * убрать, убирают закрытием вкладки — вместе со всем занятием.
   */
  readonly soundOnError: boolean;
  /**
   * Игровые элементы: достижения, задания, серии, XP и уровни.
   *
   * Выключаются целиком (ТЗ FR-GM-08). Живут здесь, а не в приватности:
   * это решение о том, что показывать на экране, а не о том, что уходит
   * наружу.
   */
  readonly gameElements: boolean;
}

/** Внешний вид по умолчанию: тема как в системе, панели на месте, звук включён. */
export const DEFAULT_APPEARANCE: Appearance = Object.freeze({
  theme: DEFAULT_THEME,
  focusMode: false,
  soundOnError: true,
  gameElements: true,
});

/**
 * С чего пользователь начал обучение языку.
 *
 * Новичок, который печатает двумя пальцами, на диагностике измеряет то, чего
 * ещё нет: ей нужен режим «Основы» (ТЗ §6, режим 2). Выбор сохраняется, чтобы
 * возвращение в приложение продолжало начатый путь, а не спрашивало заново.
 */
export const START_PATHS = ['fundamentals', 'diagnostics'] as const;
export type StartPath = (typeof START_PATHS)[number];

/** Профиль одного языка внутри пространства. */
export interface LanguageProfile {
  readonly language: LanguageCode;
  readonly keyboard: KeyboardProfile;
  readonly baseline: Baseline | null;
  readonly units: Readonly<Record<string, MasteryUnit>>;
  readonly stats: Readonly<Record<string, MasteryStat>>;
  /** Идентификаторы единиц, встречавшихся в контрольном материале. */
  readonly seenInControl: readonly string[];
  /** Лучший подтверждённый результат, WPM. */
  readonly bestVerifiedWpm: number;
  /**
   * Личные рекорды и накопленный объём набора.
   *
   * Хранятся, а не вычисляются: история занятий на клиенте не ведётся —
   * остаются только агрегаты, и восстановить по ним лучший прогон нельзя.
   */
  readonly bestCpm: number;
  readonly bestAccuracy: number;
  /** Сколько символов набрано за всё время в этом языке. */
  readonly typedCharacters: number;
  /** Календарные даты сессий (ISO 8601, только дата). */
  readonly practiceDays: readonly string[];
  /** С чего начато обучение этому языку. */
  readonly startPath: StartPath;
  /**
   * Целевой темп режима «Скорость», CPM; null — режим ещё не начинали.
   *
   * Ступень лестницы темпа переживает перезагрузку: набранная скорость — это
   * результат нескольких занятий, и начинать её заново с каждым входом
   * значило бы обесценивать их (ТЗ §6, режим 6).
   */
  readonly tempoTargetCpm: number | null;
}

/** Полное личное пространство. */
export interface Workspace {
  readonly owner: WorkspaceOwner;
  /** Версия схемы локального хранилища. */
  readonly schemaVersion: number;
  readonly uiLanguage: LanguageCode;
  readonly activeLanguage: LanguageCode;
  readonly profiles: Readonly<Record<string, LanguageProfile>>;
  readonly xpLedger: readonly XpTransaction[];
  readonly privacy: PrivacySettings;
  /** Тема интерфейса и фокус-режим. */
  readonly appearance: Appearance;
  /** Снимок доступов, полученный от сервера. Клиент его не вычисляет. */
  readonly entitlements: EntitlementSnapshot | null;
  /**
   * Полученные достижения.
   *
   * Хранятся навсегда: заработанное не отбирается, даже если показатель,
   * которым оно заработано, потом просел (ТЗ FR-SUB-06). Живут в
   * пространстве, а не в профиле языка: достижение принадлежит человеку.
   */
  readonly achievements: readonly UnlockedAchievement[];
  /**
   * Журнал занятий по дням: только сводки.
   *
   * Нужен заданиям дня и недели: накопительные счётчики отвечают «сколько
   * всего», а задание спрашивает «сколько сегодня». Глубина ограничена —
   * см. `ACTIVITY_LOG_DAYS`.
   */
  readonly activityLog: readonly DayActivity[];
  /** Календарная метка создания (ISO 8601). */
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * Текущая версия схемы локального хранилища.
 *
 * Версия 2 добавила в профиль языка `startPath`, версия 3 — `tempoTargetCpm`,
 * версия 4 — настройки внешнего вида пространства (`appearance`), версия 5 —
 * личные рекорды профиля и достижения пространства, версия 6 — журнал занятий
 * по дням.
 * Записи прежних версий читаются и дополняются значением по умолчанию: терять
 * чужой прогресс из-за нового поля нельзя (`migrateWorkspace`).
 *
 * Тема и фокус-режим живут здесь, а не в браузере, и переживают перезагрузку
 * (ТЗ FR-EN-35). Требование называет версию 4 — ту, с которой появился
 * `appearance`; схема с тех пор ушла вперёд, и номер в требовании читается как
 * «с версии 4», а не как текущий.
 */
export const WORKSPACE_SCHEMA_VERSION = 6;
